import { PLUGINS } from 'queries/query-keys';
import { useCallback, useMemo, lazy, Suspense } from 'react';
import type { ComponentProps } from 'react';
import {
  type QueryClient,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import PluginService from 'services/PluginService';
import { refreshPluginAppList } from 'store/authentication/authenticationAction';
import { PluginModal } from './PluginModal';
import useModal from '__components/Modals/useModal';
import { StyledLoader } from 'app/styles';
import TeamMemberService from 'services/TeamMemberService';
import {
  filterAutomationSteps,
  filterCalendarSources,
  filterCustomBlocks,
  filterDataAdornments,
  filterFloatingFrames,
  filterObjectSettingsMenuItems,
  filterToolbarItems,
  getFlagsForPlugin,
} from './featureFlags';
import {
  flattenReservedState,
  getEnabledState,
  mergeConfig,
  pluginMapper,
  reduceEnabledResults,
} from '@kizenapps/engine/util';
import { planFromActiveLicenses } from './planFromActiveLicenses';
import { automationMapper, AutomationStepConfigApi } from './automations';
import {
  BusinessPluginApp,
  DefaultConfig,
  FeatureFlaggedArtifacts,
  FeatureFlags,
  PluginBlockConfig,
  PluginBlockEntry,
  PluginConfig,
} from './types';
import { PluginContext } from './context';
import { useOverlaySuppression } from './overlaySuppression';
import {
  type InlinePagesRegistry,
  useInlinePages,
  useInlinePagesRegistry,
  withInlinePages,
} from './inlinePages';
import { usePluginsEnabled } from './usePluginsEnabled';
import { useBusinessPluginApps } from './useBusinessPluginApps';
import {
  AppEngineProvider,
  useCalendarSourceReset,
} from '@kizenapps/engine/react';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { toastVariant, useToast } from '__components/ToastProvider';
import { AssistantField, OnShowToastFn } from '@kizenapps/engine';
import { useHistory } from 'react-router-dom';
import FileService from 'services/FileService';
import { unsafeRootQueryClient } from '__queries/unsafe-query-client';
import { invalidate } from '__queries/invalidate';
import { useTranslation } from 'react-i18next';
import { handleAxiosResponseForPluginCall } from './network';
import { isAxiosError } from 'axios';
import { toPluginToastMessage } from './toast';
import {
  ActiveSetupSurfaceProvider,
  useCompleteSetup,
} from './useCompleteSetup';

export type { ActiveSetupSurface } from './useCompleteSetup';
export { useRegisterActiveSetupSurface } from './useCompleteSetup';

// Lazy load to prevent circular dependency since our inputs use pluginContext:
const CreateRecordModal = lazy(() =>
  import('./CreateRecordModal').then((m) => ({ default: m.CreateRecordModal }))
);

const CreateRelatedRecordModal = lazy(() =>
  import('./CreateRelatedRecordModal').then((m) => ({
    default: m.CreateRelatedRecordModal,
  }))
);

const defaultConfig: DefaultConfig = {
  floatingFrames: [],
  routablePages: [],
  dataAdornments: [],
  toolbarItems: [],
  routeScripts: [],
  automationSteps: [],
  objectSettingsMenuItems: [],
  calendarSources: [],
  pluginBlocks: [],
};

const blockMapper = (
  custom_blocks: PluginBlockConfig[] | undefined,
  apiName: string,
  plugin: { name: string; thumbnail?: string }
): PluginBlockEntry[] => {
  if (!custom_blocks?.length) {
    return [];
  }
  return [
    {
      plugin_api_name: apiName,
      plugin_name: plugin.name,
      thumbnail: plugin.thumbnail,
      blocks: custom_blocks.map((b) => ({ ...b, plugin_api_name: apiName })),
    },
  ];
};

// Its own component so a block registering views re-renders only the modal,
// not every PluginContext consumer
const HostPluginModal = ({
  registry,
  routablePages,
  ...props
}: Omit<ComponentProps<typeof PluginModal>, 'pages'> & {
  registry: InlinePagesRegistry;
  routablePages: DefaultConfig['routablePages'];
}) => {
  const inlinePages = useInlinePages(registry);
  const pages = useMemo(
    () => withInlinePages(routablePages, inlinePages),
    [routablePages, inlinePages]
  );
  return <PluginModal {...props} pages={pages} />;
};

export const invalidatePluginArtifacts = (
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  pluginAppId?: string,
  throwOnError?: boolean
) =>
  Promise.all(
    [
      PLUGINS.GET_PLUGIN_APPS().filter(Boolean),
      PLUGINS.GET_JS_ACTION_TEMPLATE_ASSOCIATIONS().filter(Boolean),
      PLUGINS.GET_EVENTS().filter(Boolean),
      ...(pluginAppId ? [PLUGINS.GET_USER_CONFIG(pluginAppId)] : []),
    ].map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }, { throwOnError })
    )
  );

export const PluginContextWrapper = ({ children }: { children: any }) => {
  const pluginApps: any[] = useSelector(
    (s: any) => s?.authentication?.pluginApps
  );

  const enablePlugins = usePluginsEnabled();

  const { hiddenByEditor, suppressOverlays } = useOverlaySuppression();

  const inlinePagesRegistry = useInlinePagesRegistry();

  const dispatch = useDispatch<any>();

  const [integrationModalProps, , integrationModalTriggerProps] = useModal({});

  const { onConfirm, show, onHide } = integrationModalProps;

  const { show: showPrompt, showing } = integrationModalTriggerProps;

  const installedPluginAPINamesToIds = useMemo(() => {
    return (
      pluginApps?.reduce?.((acc, plugin) => {
        return {
          ...acc,
          [plugin.api_name]: plugin.id,
        };
      }, {}) ?? {}
    );
  }, [pluginApps]);

  const business = useSelector((s: any) => s.authentication.chosenBusiness);

  const plan = useMemo(
    () => planFromActiveLicenses(business?.active_licenses),
    [business]
  );

  const entitlements = useMemo(
    () => business?.entitlements ?? {},
    [business]
  );

  const reservedState = useMemo(
    () => flattenReservedState(plan, entitlements),
    [plan, entitlements]
  );

  const {
    data = {},
    isLoading: pluginsLoading,
    isFetched: hasFetchedBootstrap,
    refetch,
  } = useQuery<PluginConfig>({
    queryKey: PLUGINS.LIST,
    queryFn: async () => {
      // we smother the error
      try {
        return PluginService.get();
      } catch (error) {}
    },
    enabled: enablePlugins,
    throwOnError: false,
  });

  const apiNameKeys = useMemo(() => {
    return Object.keys(data ?? {}).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const {
    data: businessPluginApps,
    isLoading: businessPluginAppsLoading,
    isFetched: hasFetchedBusinessPluginApps,
  } = useBusinessPluginApps();

  const { businessPluginAppsByApiName, businessPluginAppsById } =
    useMemo(() => {
      const businessPluginAppsByApiName: Record<string, BusinessPluginApp> = {};
      const businessPluginAppsById: Record<string, BusinessPluginApp> = {};

      businessPluginApps?.forEach((curr) => {
        businessPluginAppsByApiName[curr.plugin_app.api_name] = curr;
        businessPluginAppsById[curr.id] = curr;
      });

      return { businessPluginAppsByApiName, businessPluginAppsById };
    }, [businessPluginApps]);

  const {
    data: rawFeatureFlags_DO_NOT_USE_UNWRAPPED,
    isLoading: featureFlagsLoading,
    refetch: refetchFeatureFlagsWorker,
    isFetched: hasCalculatedFeatureFlags,
  } = useQuery<FeatureFlags>({
    queryKey: PLUGINS.ENABLED_KEYS(apiNameKeys, plan, entitlements),
    queryFn: async () => {
      const results = await Promise.all(
        apiNameKeys.map(async (pluginApiName) => {
          const plugin = data[pluginApiName];
          const businessPluginApp = businessPluginAppsByApiName[pluginApiName];

          const baseConfig = businessPluginApp?.plugin_app?.base_config || {};

          const businessConfig = plugin?.business_config || {};
          const userConfig = plugin?.employee_config?.user_config || {};

          const businessCleanConfig = businessConfig.__kizen_clean_config || {};
          const userCleanConfig = userConfig.__kizen_clean_config || {};

          const businessRawConfig =
            businessConfig.__kizen_setup_assistant_values || {};
          const userRawConfig = userConfig.__kizen_setup_assistant_values || {};

          const businessConfigFields = baseConfig.setup_assistant?.fields;
          const userConfigFields = baseConfig.user_setup_assistant?.fields;

          const mergedBusinessConfig = mergeConfig(
            businessCleanConfig,
            baseConfig.disabled_keys || [],
            businessRawConfig,
            businessConfigFields as AssistantField[]
          );

          const mergedUserConfig = mergeConfig(
            userCleanConfig,
            baseConfig.disabled_keys || [],
            userRawConfig,
            userConfigFields
          );

          const state: Record<string, any> = { ...reservedState };

          if (mergedBusinessConfig) {
            Object.keys(mergedBusinessConfig).forEach((configKey) => {
              state[`config__${configKey}`] = mergedBusinessConfig[configKey];
            });
          }

          if (mergedUserConfig) {
            Object.keys(mergedUserConfig).forEach((configKey) => {
              state[`userConfig__${configKey}`] = mergedUserConfig[configKey];
            });
          }

          const {
            floating_frames,
            automation_action_configs,
            data_adornments,
            object_settings_menu_items,
            toolbar_items,
            calendar_sources,
            custom_blocks,
          } = plugin;

          const floatingFrameClauses = floating_frames.filter((f) => f.when);
          const automationActionClauses = automation_action_configs.filter(
            (a) => a.when
          );
          const dataAdornmentClauses = data_adornments.filter((d) => d.when);
          const objectSettingsClauses = object_settings_menu_items.filter(
            (o) => o.when
          );
          const toolbarItemClauses = toolbar_items.filter((t) => t.when);
          const calendarSourceClauses = calendar_sources.filter((c) => c.when);
          const customBlockClauses = (custom_blocks ?? []).filter(
            (b) => b.when
          );

          const floatingFramePromises = floatingFrameClauses.map(async (f) => {
            const enabled = await getEnabledState(f.when, state);

            return {
              api_name: f.api_name,
              enabled,
            };
          });

          const automationActionPromises = automationActionClauses.map(
            async (a) => {
              const enabled = await getEnabledState(a.when, state);

              return {
                api_name: a.action_step_api_name,
                enabled,
              };
            }
          );

          const dataAdornmentPromises = dataAdornmentClauses.map(async (d) => {
            const enabled = await getEnabledState(d.when, state);

            return {
              api_name: d.field_type,
              enabled,
            };
          });

          const objectSettingsPromises = objectSettingsClauses.map(
            async (o) => {
              const enabled = await getEnabledState(o.when, state);

              return {
                api_name: o.api_name,
                enabled,
              };
            }
          );

          const toolbarItemPromises = toolbarItemClauses.map(async (t) => {
            const enabled = await getEnabledState(t.when, state);

            return {
              api_name: t.api_name,
              enabled,
            };
          });

          const calendarSourcePromises = calendarSourceClauses.map(
            async (c) => {
              const enabled = await getEnabledState(c.when, state);

              return {
                api_name: c.api_name,
                enabled,
              };
            }
          );

          const customBlockPromises = customBlockClauses.map(async (b) => {
            const enabled = await getEnabledState(b.when, state);

            return {
              api_name: b.api_name,
              enabled,
            };
          });

          const [
            floatingFrameResults,
            automationActionResults,
            dataAdornmentResults,
            objectSettingsResults,
            toolbarItemResults,
            calendarSourceResults,
            customBlockResults,
          ] = await Promise.all([
            Promise.all(floatingFramePromises),
            Promise.all(automationActionPromises),
            Promise.all(dataAdornmentPromises),
            Promise.all(objectSettingsPromises),
            Promise.all(toolbarItemPromises),
            Promise.all(calendarSourcePromises),
            Promise.all(customBlockPromises),
          ]);

          const enabledMap: Record<
            FeatureFlaggedArtifacts,
            Record<string, boolean>
          > = {
            floatingFrames: reduceEnabledResults(floatingFrameResults),
            automationActions: reduceEnabledResults(automationActionResults),
            dataAdornments: reduceEnabledResults(dataAdornmentResults),
            objectSettings: reduceEnabledResults(objectSettingsResults),
            toolbarItems: reduceEnabledResults(toolbarItemResults),
            calendarSources: reduceEnabledResults(calendarSourceResults),
            customBlocks: reduceEnabledResults(customBlockResults),
          };

          return {
            plugin_api_name: pluginApiName,
            enabledMap,
          };
        })
      );

      const resultMap = results.reduce((acc, curr) => {
        return {
          ...acc,
          [curr.plugin_api_name]: curr.enabledMap,
        };
      }, {});

      return resultMap;
    },
    enabled:
      hasFetchedBootstrap && hasFetchedBusinessPluginApps && enablePlugins,
  });

  const refetchFeatureFlags = useCallback(async () => {
    await refetchFeatureFlagsWorker();
  }, [refetchFeatureFlagsWorker]);

  const configQueries = useQueries({
    queries: apiNameKeys
      .filter((apiName) =>
        Boolean(apiName && installedPluginAPINamesToIds[apiName])
      )
      .map((apiName) => ({
        queryKey: PLUGINS.GET_USER_CONFIG(
          installedPluginAPINamesToIds[apiName]
        ),
        queryFn: async () => {
          try {
            return await TeamMemberService.getPluginConfig(
              installedPluginAPINamesToIds[apiName]
            );
          } catch (error) {
            showToast({
              variant: toastVariant.FAILURE,
              message: t(
                'There was a problem loading installed apps. Please contact your administrator.'
              ),
            });
            const status = isAxiosError(error) ? error.response?.status : null;
            if (!status || ![401, 403, 404].includes(status)) {
              monitoringExceptionHelper(error);
            }
            return { config: {} };
          }
        },
        enabled: enablePlugins,
      })),
  });

  const configsLoading = configQueries.some((c) => c.isLoading);

  const isLoading =
    (pluginsLoading ||
      configsLoading ||
      featureFlagsLoading ||
      businessPluginAppsLoading) &&
    // If something starts fetching plugin assets outside the boostrap process, it might flip one of these flags to true.
    // We want to make sure isLoading only becomes true when there are plugins enabled, otherwise we get loaders in strange places
    enablePlugins;

  const userConfigs = useMemo(() => {
    const data = configQueries.map((c, i) => {
      return {
        config: c.data?.config?.user_config ?? {},
        api_name: apiNameKeys[i],
      };
    });
    return data;
  }, [configQueries, apiNameKeys]);

  const userConfigsByApiName = useMemo(() => {
    return userConfigs.reduce((acc, item) => {
      return {
        ...acc,
        [item.api_name]: item.config,
      };
    }, {});
  }, [userConfigs]);

  const pluginNames = useMemo(() => {
    return Object.keys(data ?? {});
  }, [data]);

  // We need to wrap the feature flags in a proxy layer to handle undefined and fallback
  // values, so `rawFeatureFlags_DO_NOT_USE_UNWRAPPED` should only ever be used right here
  const featureFlags = useMemo(() => {
    return new Proxy(rawFeatureFlags_DO_NOT_USE_UNWRAPPED || {}, {
      get(pluginMap, pluginApiName) {
        return getFlagsForPlugin(pluginApiName as string, pluginMap);
      },
    });
  }, [rawFeatureFlags_DO_NOT_USE_UNWRAPPED]);

  const result = useMemo(() => {
    const result = pluginNames.reduce((acc, pluginApiName) => {
      const plugin = data[pluginApiName];

      return {
        ...acc,
        floatingFrames: [
          ...acc.floatingFrames,
          ...(plugin.floating_frames?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        routablePages: [
          ...acc.routablePages,
          ...(plugin.routable_pages?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        dataAdornments: [
          ...acc.dataAdornments,
          ...(plugin.data_adornments?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        toolbarItems: [
          ...acc.toolbarItems,
          ...(plugin.toolbar_items?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
          ...(plugin.routable_pages
            ?.filter((c) => c.is_toolbar_item)
            ?.map((config) => {
              return {
                ...pluginMapper(config, pluginApiName, plugin),
                icon: config.toolbar_icon,
                label: config.name,
                color: config.toolbar_color,
                script: '',
              };
            }) ?? []),
        ],
        routeScripts: [
          ...acc.routeScripts,
          ...(plugin.browser_route_scripts?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        automationSteps: [
          ...acc.automationSteps,
          ...automationMapper(
            plugin.automation_action_configs as unknown as AutomationStepConfigApi[],
            pluginApiName,
            plugin
          ),
        ],
        pluginBlocks: [
          ...acc.pluginBlocks,
          ...blockMapper(plugin.custom_blocks, pluginApiName, plugin),
        ],
        objectSettingsMenuItems: [
          ...acc.objectSettingsMenuItems,
          ...(plugin.object_settings_menu_items?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
        calendarSources: [
          ...acc.calendarSources,
          ...(plugin.calendar_sources?.map((config) =>
            pluginMapper(config, pluginApiName, plugin)
          ) ?? []),
        ],
      };
    }, defaultConfig);

    return {
      ...result,
      floatingFrames: filterFloatingFrames(result.floatingFrames, featureFlags),
      dataAdornments: filterDataAdornments(result.dataAdornments, featureFlags),
      automationSteps: filterAutomationSteps(
        result.automationSteps,
        featureFlags
      ),
      objectSettingsMenuItems: filterObjectSettingsMenuItems(
        result.objectSettingsMenuItems,
        featureFlags
      ),
      toolbarItems: filterToolbarItems(result.toolbarItems, featureFlags),
      calendarSources: filterCalendarSources(
        result.calendarSources,
        featureFlags
      ),
      pluginBlocks: filterCustomBlocks(result.pluginBlocks, featureFlags),
    };
  }, [data, pluginNames, featureFlags]);

  const {
    floatingFrames,
    routablePages,
    dataAdornments,
    toolbarItems: unsortedToolbarItems,
    routeScripts,
    automationSteps: unsortedAutomationSteps,
    objectSettingsMenuItems: unsortedObjectSettingsMenuItems,
    calendarSources: unsortedCalendarSources,
    pluginBlocks: unsortedPluginBlocks,
  } = result;

  const objectSettingsMenuItems = useMemo(() => {
    return unsortedObjectSettingsMenuItems.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [unsortedObjectSettingsMenuItems]);

  const toolbarItems = useMemo(() => {
    return unsortedToolbarItems.sort((a, b) => a.label.localeCompare(b.label));
  }, [unsortedToolbarItems]);

  const calendarSources = useMemo(() => {
    return unsortedCalendarSources.sort((a, b) => a.name.localeCompare(b.name));
  }, [unsortedCalendarSources]);

  const toolbarItemsByFullId = useMemo(() => {
    return toolbarItems.reduce((acc, item) => {
      return {
        ...acc,
        [`${item.plugin_api_name}-${item.api_name}`]: item,
      };
    }, {});
  }, [toolbarItems]);

  const automationSteps = useMemo(() => {
    return unsortedAutomationSteps.sort((a, b) => a.name.localeCompare(b.name));
  }, [unsortedAutomationSteps]);

  const pluginBlocks = useMemo(() => {
    return unsortedPluginBlocks
      .slice()
      .sort((a, b) => a.plugin_name.localeCompare(b.plugin_name));
  }, [unsortedPluginBlocks]);

  const resetCalendarSource = useCalendarSourceReset();

  const maybeRefetchCalendarData = useCallback(
    (pluginApiName: string) => {
      const matchingSources = calendarSources.filter(
        (source) => source.plugin_api_name === pluginApiName
      );

      if (matchingSources.length === 0) {
        return;
      }

      matchingSources.forEach((source) => {
        resetCalendarSource(pluginApiName, source.api_name);
      });
    },
    [resetCalendarSource, calendarSources]
  );

  const fullRefetch = useCallback(
    async (pluginApiName?: string, throwOnError?: boolean) => {
      const res = await refetch({ throwOnError });
      const [error] = await dispatch(refreshPluginAppList());
      if (throwOnError && error) throw error;
      if (pluginApiName) {
        maybeRefetchCalendarData(pluginApiName);
      }

      return res;
    },
    [refetch, dispatch, maybeRefetchCalendarData]
  );

  const queryClient = useQueryClient();

  const refreshPluginArtifacts = useCallback(
    async (
      pluginApiName?: string,
      options?: { pluginAppId?: string; throwOnError?: boolean }
    ) => {
      const pluginAppId =
        options?.pluginAppId ??
        (pluginApiName
          ? installedPluginAPINamesToIds[pluginApiName]
          : undefined);

      await Promise.all([
        fullRefetch(pluginApiName, options?.throwOnError).then(() =>
          refetchFeatureFlagsWorker({ throwOnError: options?.throwOnError })
        ),
        invalidatePluginArtifacts(
          queryClient,
          pluginAppId,
          options?.throwOnError
        ),
      ]);
    },
    [
      fullRefetch,
      refetchFeatureFlagsWorker,
      queryClient,
      installedPluginAPINamesToIds,
    ]
  );

  const user = useSelector((s: any) => s.authentication.user);
  const teamMember = useSelector((s: any) => s.authentication.teamMember);
  const clientObject = useSelector(getBusinessClientObject);
  const sessionInvalid = useSelector((s: any) => s.authentication.showBlocker);

  const history = useHistory();

  const onNavigate = useCallback(
    (path: string, args?: { replace?: boolean }) => {
      if (args?.replace) {
        history.replace(path);
      } else {
        history.push(path);
      }
    },
    [history]
  );

  const [showToast, , clearToasts] = useToast();

  const showPluginToast = useCallback<OnShowToastFn>(
    ({ message, ...toast }) =>
      showToast({ ...toast, message: toPluginToastMessage(message) }),
    [showToast]
  );

  const getPendingCacheCount = useCallback((search: string) => {
    return unsafeRootQueryClient.isFetching({
      predicate: (q) => q.queryKey.includes(search),
    });
  }, []);

  const invalidateCache = useCallback(
    (category: 'entity' | 'timeline', entityId: string) => {
      if (category === 'entity') {
        unsafeRootQueryClient.invalidateQueries({
          predicate: (q) => {
            return q.queryKey.includes(entityId);
          },
        });
        unsafeRootQueryClient.invalidateQueries({
          queryKey: [
            { scope: ['record-details', 'related-object-block', entityId] },
          ],
        });
      } else if (category === 'timeline') {
        invalidate.TIMELINE.RECORD(entityId);
      }
    },
    []
  );

  const onError = useCallback(
    (error: unknown, extra?: any) =>
      monitoringExceptionHelper(error, { extra }),
    []
  );

  const { onCompleteSetup, activeSetupSurfaces } = useCompleteSetup(
    refreshPluginArtifacts
  );

  const { t } = useTranslation();

  return (
    <AppEngineProvider
      user={user}
      teamMember={teamMember}
      business={business}
      clientObject={clientObject}
      onNavigate={onNavigate}
      monitoringExceptionHelper={onError}
      showToast={showPluginToast}
      clearToasts={clearToasts}
      modal={{
        showing,
        show,
        onConfirm,
        onHide,
        showPrompt,
      }}
      performRequest={handleAxiosResponseForPluginCall}
      appPath={import.meta.env.VITE_API_BASE_PATH}
      bootstrapPlugins={pluginApps}
      performFileUpload={FileService.upload}
      getPendingCacheCount={getPendingCacheCount}
      invalidateCache={invalidateCache}
      userConfigs={userConfigs}
      onCompleteSetup={onCompleteSetup}
      t={t}
    >
      {({
        handleCreateRecordComplete,
        handleCreateRelatedRecordComplete,
        showCreateRecordModal,
        showCreateRelatedRecordModal,
        createRecordModalObjectId,
        createRelatedRecordModalObjectId,
        createRelatedRecordModalRelatedEntityId,
        showLoadingIndicator,
        hasFinishedBootstrapping,
        derivedModalState,
        showPluginModal,
        pluginApiName,
        ...rest
      }) => {
        return (
          <PluginContext.Provider
            value={{
              isFetched: hasFetchedBootstrap,
              isLoading,
              sessionInvalid,
              pluginNames,
              floatingFrames: isLoading ? [] : floatingFrames,
              routablePages,
              dataAdornments,
              calendarSources,
              refetch,
              fullRefetch,
              refreshPluginArtifacts,
              installedPluginAPINamesToIds,
              plugins: data,
              toolbarItems,
              toolbarItemsByFullId,
              routeScripts,
              automationSteps: isLoading ? [] : automationSteps,
              pluginBlocks: isLoading ? [] : pluginBlocks,
              objectSettingsMenuItems,
              userConfigsByApiName,
              featureFlags,
              refetchFeatureFlags,

              // Both of these loading flags stay false if there are no plugins enabled, so we don't block
              // loading in businesses with no plugins. This flag should also be ignored if no enabled
              // plugins are flagged as requiring blocking the app's loading
              featureFlagsLoading:
                !hasCalculatedFeatureFlags && !hasFinishedBootstrapping,
              businessPluginAppsByApiName,
              plan,
              entitlements,
              businessPluginAppsById,
              hiddenByEditor,
              suppressOverlays,
              registerInlinePages: inlinePagesRegistry.register,
              ...rest,
            }}
          >
            <ActiveSetupSurfaceProvider value={activeSetupSurfaces}>
              {showLoadingIndicator ? <StyledLoader loading /> : null}
              {children}
              {showPluginModal ? (
                <HostPluginModal
                  modalState={derivedModalState}
                  pluginApiName={pluginApiName}
                  routablePages={routablePages}
                  registry={inlinePagesRegistry}
                />
              ) : null}
              {showCreateRecordModal ? (
                <Suspense fallback={null}>
                  <CreateRecordModal
                    objectId={createRecordModalObjectId}
                    onComplete={handleCreateRecordComplete}
                  />
                </Suspense>
              ) : null}
              {showCreateRelatedRecordModal ? (
                <Suspense fallback={null}>
                  <CreateRelatedRecordModal
                    objectId={createRelatedRecordModalObjectId}
                    relatedEntityId={createRelatedRecordModalRelatedEntityId}
                    onComplete={handleCreateRelatedRecordComplete}
                  />
                </Suspense>
              ) : null}
            </ActiveSetupSurfaceProvider>
          </PluginContext.Provider>
        );
      }}
    </AppEngineProvider>
  );
};
